import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { NavigateFunction } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Plus, XCircle } from "lucide-react";

import { PRO014_BULK_ACTION, PRO014_STORE_MODE } from "@/@types/campaign";
import type { IPro014CampaignItem } from "@/@types/campaign";
import { Button } from "@/components/ui/button";
import { useGetUnifiedCampaignByCodeQuery } from "@/services/campaign";
import {
  useGetPro014AvailableStoresQuery,
  useGetPro014ItemsPaginatedQuery,
} from "@/services/campaign-pro014";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";

import { AddItemModal } from "../components/add-item-modal";
import { CampaignConflictModal } from "../components/campaign-conflict-modal";
import { CAMPAIGN_STATUS } from "../components/edit-campaign/constants";
import { ITEMS_WITHOUT_GRADE_TO_ENABLE } from "../components/grade/grade-messages";
import { GradeRestrictionSwitch } from "../components/grade/grade-restriction-switch";
import { GradeTargetModalConnected } from "../components/grade/grade-target-modal-connected";
import { ItemsWithoutGradeAlert } from "../components/grade/items-without-grade-alert";
import { CAMPAIGNS_LIST_PATH } from "../create/constants";
import { useGradeRestrictionToggle } from "../hooks/use-grade-restriction-toggle";
import type { GradeRestrictionToggleState } from "../types";
import { BulkActionDialog } from "./components/bulk-action-dialog";
import { ProductsFiltersBar } from "./components/products-filters-bar";
import { ProductsTable } from "./components/products-table";
import { DEFAULT_CAMPAIGN_TYPE, PRODUCTS_TEXTS } from "./constants";
import { useProductsActions } from "./hooks/use-products-actions";
import { useProductsFilters } from "./hooks/use-products-filters";
import type { GradeItem, ProductsActionsState } from "./types";

interface StatCardProps {
  label: string;
  value: number;
  valueClass: string;
}

function StatCard({ label, value, valueClass }: StatCardProps): JSX.Element {
  return (
    <div className="rounded-lg border bg-white p-4 shadow dark:bg-gray-950">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold ${valueClass}`}>{value}</div>
    </div>
  );
}

export function CampaignProductsPage(): JSX.Element {
  const { code = "", type = DEFAULT_CAMPAIGN_TYPE } = useParams<{ code: string; type: string }>();
  const campaignCode: number = Number(code);
  const campaignType: string = type.toUpperCase();
  const navigate: NavigateFunction = useNavigate();
  const [gradeItem, setGradeItem] = useState<GradeItem | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);

  const { filters, updateFilter } = useProductsFilters();
  const { data: unifiedCampaign } = useGetUnifiedCampaignByCodeQuery(`${campaignType}-${code}`);
  const isLegacy: boolean = unifiedCampaign?.storeMode === PRO014_STORE_MODE.LEGACY;
  const gradeRestricted: boolean = Boolean(unifiedCampaign?.gradeRestricted);
  const isExpired: boolean = unifiedCampaign?.visualStatus === CAMPAIGN_STATUS.EXPIRED;

  const { data: itemsPage, isLoading, isFetching, refetch } = useGetPro014ItemsPaginatedQuery({
    code: campaignCode,
    page: filters.page,
    limit: filters.limit,
    status: filters.status,
    search: filters.search,
    sort: filters.sort,
    storeCode: isLegacy ? filters.storeCode : undefined,
  });
  const { data: availableStores = [] } = useGetPro014AvailableStoresQuery(campaignCode, {
    skip: !isLegacy,
  });

  const actions: ProductsActionsState = useProductsActions(campaignCode);
  const restriction: GradeRestrictionToggleState = useGradeRestrictionToggle(campaignCode);
  const items: IPro014CampaignItem[] = itemsPage?.items ?? [];

  return (
    <PageLayout>
      <PageTitle
        title={PRODUCTS_TEXTS.TITLE(campaignType, code)}
        description={PRODUCTS_TEXTS.DESCRIPTION}
      />
      <ContentContainer>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <Button variant="outline" onClick={() => navigate(CAMPAIGNS_LIST_PATH)} className="rounded-full">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
            {PRODUCTS_TEXTS.BACK}
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            {PRODUCTS_TEXTS.ADD_PRODUCT}
          </Button>
        </div>

        <div className="mb-4 space-y-2">
          <GradeRestrictionSwitch
            checked={gradeRestricted}
            onCheckedChange={(enabled: boolean) => void restriction.toggle(enabled)}
            disabled={restriction.isSaving || !unifiedCampaign || isExpired}
          />
          <ItemsWithoutGradeAlert
            itemCodes={restriction.itemsWithoutGrade}
            description={ITEMS_WITHOUT_GRADE_TO_ENABLE}
          />
        </div>

        {itemsPage && (
          <div className="mb-6 grid grid-cols-3 gap-4">
            <StatCard label={PRODUCTS_TEXTS.TOTAL} value={itemsPage.total} valueClass="text-[#2E0D1E]" />
            <StatCard
              label={PRODUCTS_TEXTS.ACTIVE}
              value={items.filter((item) => item.isActive).length}
              valueClass="text-green-600"
            />
            <StatCard
              label={PRODUCTS_TEXTS.INACTIVE}
              value={items.filter((item) => !item.isActive).length}
              valueClass="text-gray-400"
            />
          </div>
        )}

        <ProductsFiltersBar
          filters={filters}
          showStoreFilter={isLegacy}
          availableStores={availableStores}
          onFilterChange={updateFilter}
        />

        {actions.selectedItems.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/20">
            <span className="font-medium">{PRODUCTS_TEXTS.SELECTED(actions.selectedItems.length)}</span>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => actions.requestBulkAction(PRO014_BULK_ACTION.ACTIVATE)}
                variant="outline"
                disabled={actions.isBulkLoading}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden />
                {PRODUCTS_TEXTS.ACTIVATE_SELECTED}
              </Button>
              <Button
                onClick={() => actions.requestBulkAction(PRO014_BULK_ACTION.DEACTIVATE)}
                variant="destructive"
                disabled={actions.isBulkLoading}
              >
                <XCircle className="mr-2 h-4 w-4" aria-hidden />
                {PRODUCTS_TEXTS.DEACTIVATE_SELECTED}
              </Button>
            </div>
          </div>
        )}

        <ProductsTable
          itemsPage={itemsPage}
          isLoading={isLoading || isFetching}
          showStoreColumn={isLegacy}
          page={filters.page}
          limit={filters.limit}
          actions={actions}
          onEditGrade={setGradeItem}
          onFilterChange={updateFilter}
        />

        {gradeItem && (
          <GradeTargetModalConnected
            campaignCode={campaignCode}
            itemCode={gradeItem.itemCode}
            itemName={gradeItem.itemName}
            gradeRestricted={gradeRestricted}
            onGradeSaved={restriction.handleGradeSaved}
            onClose={() => setGradeItem(null)}
          />
        )}

        <AddItemModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          campaignCode={campaignCode}
          campaignType={campaignType}
          numLists={unifiedCampaign?.numLists}
          gradeRestricted={gradeRestricted}
          onSuccess={() => void refetch()}
        />

        <BulkActionDialog
          action={actions.bulkAction}
          selectedCount={actions.selectedItems.length}
          isLoading={actions.isBulkLoading}
          onCancel={actions.cancelBulkAction}
          onConfirm={actions.confirmBulkAction}
        />

        <CampaignConflictModal
          isOpen={actions.conflicts.length > 0 || restriction.conflicts.length > 0}
          conflicts={[...actions.conflicts, ...restriction.conflicts]}
          onClose={() => {
            actions.closeConflicts();
            restriction.closeConflicts();
          }}
          onGoBack={() => {
            actions.closeConflicts();
            restriction.closeConflicts();
          }}
        />
      </ContentContainer>
    </PageLayout>
  );
}
